import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, XCircle, AlertTriangle, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function TerminalSalePanel({ sale, onUpdate, onApproved, onClose }) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sale.status !== "pending") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/terminal/sales/${sale.id}`);
        onUpdate(data);
        if (data.status === "approved" && data.sessionId) {
          const { data: session } = await api.get(`/sessions/${data.sessionId}`);
          toast.success(`Approved — paid $${data.amount.toFixed(2)}`);
          onApproved(session);
        }
      } catch {
        /* keep polling */
      }
    }, 2000);
    return () => clearInterval(t);
  }, [sale.id, sale.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/terminal/sales/${sale.id}/${kind}`);
      if (kind === "cancel" && sale.status !== "pending") return onClose();
      onUpdate(data);
      if (kind === "cancel") toast.message("Cancel sent to terminal");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const pending = sale.status === "pending";
  const unknown = sale.status === "unknown";

  return (
    <div data-testid="terminal-sale-panel" className="space-y-5 py-2 text-center">
      {pending ? (
        <Smartphone className="mx-auto h-14 w-14 animate-pulse text-[#d946ef]" />
      ) : unknown ? (
        <AlertTriangle className="mx-auto h-14 w-14 text-amber-400" />
      ) : (
        <XCircle className="mx-auto h-14 w-14 text-red-400" />
      )}
      <div>
        <p data-testid="terminal-sale-status" className="text-sm uppercase tracking-[0.2em] text-slate-400">
          {pending ? "Waiting for card on terminal" : unknown ? "Result unknown" : "Not approved"}
        </p>
        <p data-testid="terminal-sale-amount" className="mt-2 font-data text-3xl font-black text-slate-100">
          ${Number(sale.amount).toFixed(2)}
        </p>
        <p className="font-data text-xs text-slate-500">
          {sale.days > 1 || sale.tenancyType === "Daily" ? `${sale.days} day${sale.days > 1 ? "s" : ""} · ` : ""}
          Ref {sale.referenceId} · Ticket {sale.ticketNumber}
        </p>
      </div>
      <p
        data-testid="terminal-sale-message"
        className={`rounded-lg border p-3 text-sm ${
          pending ? "border-slate-800 bg-slate-950 text-slate-300"
            : unknown ? "border-amber-500/40 bg-amber-950/30 text-amber-200"
            : "border-red-500/40 bg-red-950/30 text-red-200"
        }`}
      >
        {pending && <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />}
        {sale.message}
        {!pending && " — the stay is not paid and the spot is still open."}
      </p>
      {pending ? (
        <Button data-testid="terminal-sale-cancel" onClick={() => act("cancel")} disabled={busy} variant="outline"
          className="h-12 w-full border-slate-700 bg-slate-950 font-bold text-slate-200 hover:bg-slate-800">
          Cancel on terminal
        </Button>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Button data-testid="terminal-sale-retry" onClick={() => act("retry")} disabled={busy}
            className="h-12 bg-[#A0148C] font-bold hover:bg-[#80106F]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : unknown ? "Re-check / Try again" : "Try again"}
          </Button>
          <Button data-testid="terminal-sale-close" onClick={() => act("cancel")} disabled={busy} variant="outline"
            className="h-12 border-slate-700 bg-slate-950 font-bold text-slate-200 hover:bg-slate-800">
            Close
          </Button>
        </div>
      )}
    </div>
  );
}
